import { _ as __decorate, a as __metadata, F as FormControl, b as FormControlController } from '../index-BsUQkm6O.js';
import { css, html } from 'lit';
import { property, queryAssignedElements, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import 'lit/directive.js';

var styles = css`*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

:root {
  --bsi-form-control-height: 2.5rem;
  --bsi-form-control-spacing: var(--bsi-spacing-xxs);
  --bsi-form-control-background-color: var(--bsi-color-background-inverse);
  --bsi-form-control-border-color: var(--bsi-color-border-secondary);
  --bsi-form-control-border-radius: var(--bsi-radius-smooth);
  --bsi-form-control-placeholder-color: var(--bsi-color-text-muted);
  --bsi-form-control-label-color: var(--bsi-color-text-base);
  --bsi-form-control-text-color: var(--bsi-color-text-secondary);
  --bsi-form-control-font-size: var(--bsi-body-font-size);
  --bsi-form-group-spacing-y: var(--bsi-spacing-m);
  --bsi-form-checkbox-border-color: var(--bsi-color-border-secondary);
  --bsi-form-checkbox-border-radius: var(--bsi-radius-smooth);
  --bsi-form-checked-color: var(--bsi-color-background-primary);
}

input[readonly],
textarea[readonly],
select[readonly] {
  --bsi-form-control-border-color: var(--bsi-color-border-subtle);
  --bsi-form-control-background-color: var(--bsi-color-background-muted);
  cursor: not-allowed;
}

input,
textarea,
select {
  display: block;
  width: 100%;
  padding: var(--bsi-form-control-spacing);
  border: 1px solid var(--bsi-form-control-border-color);
  border-radius: var(--bsi-form-control-border-radius);
  background-color: var(--bsi-form-control-background-color);
  color: var(--bsi-form-control-text-color);
  font-size: var(--bsi-form-control-font-size);
}
input.disabled, input:disabled,
textarea.disabled,
textarea:disabled,
select.disabled,
select:disabled {
  border-color: var(--bsi-color-border-disabled);
  opacity: 1;
  background-color: var(--bsi-color-background-disabled);
  color: var(--bsi-color-text-disabled);
}

/* stylelint-disable-next-line no-duplicate-selectors */
input:focus,
textarea:focus {
  outline: 3px solid transparent;
  outline-offset: 3px;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
}

input::file-selector-button {
  margin: -0.375rem -0.75rem;
  padding: 0.375rem 0.75rem;
  border-width: 0;
  border-style: solid;
  border-radius: 0;
  border-color: inherit;
  color: hsl(0, 0%, 10%);
  pointer-events: none;
  margin-inline-end: 0.75rem;
  border-inline-end-width: 0;
  background-color: hsl(0, 0%, 100%);
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
@media (prefers-reduced-motion: reduce) {
  input::file-selector-button {
    transition: none;
  }
}
input:hover:not(:disabled):not([readonly])::file-selector-button {
  background-color: rgb(242.25, 242.25, 242.25);
}
input[type=file] {
  overflow: hidden;
}
input[type=file]:not(:disabled):not([readonly]) {
  cursor: pointer;
}
input::-webkit-date-and-time-value {
  height: 1.5em;
}

select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='hsl%280, 0%, 32%%29' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right var(--bsi-form-control-spacing) center;
  background-size: 16px 12px;
  appearance: none;
}
select:focus {
  border-color: hsl(210, 17%, 44%);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(0, 102, 204, 0.25);
}
select[multiple], select[size]:not([size="1"]) {
  padding-right: 0.75rem;
}
select:disabled {
  background-color: hsl(0, 0%, 90%);
}
select:disabled:hover {
  cursor: not-allowed;
}
select:-moz-focusring {
  color: transparent;
  text-shadow: 0 0 0 hsl(0, 0%, 10%);
}
select option {
  font-weight: normal;
}
select {
  /* stylelint-disable-next-line no-duplicate-selectors */ /* TO DO check these styles */
}
select:disabled {
  opacity: 1;
  background-color: hsl(210, 3%, 85%);
}

textarea {
  height: auto;
  font-size: var(--bsi-body-font-size);
  line-height: 1.5;
}

label {
  display: inline-block;
  width: auto;
  max-width: 100%;
  margin-bottom: var(--bsi-spacing-xxs);
  color: var(--bsi-form-control-label-color);
  font-size: var(--bsi-label-font-size-s);
  font-weight: var(--bsi-font-weight-solid);
  line-height: var(--bsi-label-leading);
}

/* stylelint-disable-next-line no-duplicate-selectors */
input,
textarea {
  outline: 0;
  box-shadow: none;
  transition: none;
  appearance: none;
}

input[type=date],
input[type=datetime-local],
input[type=time] {
  display: flex;
}

fieldset legend {
  margin-bottom: var(--bsi-spacing-s);
  padding: 0 var(--bsi-form-input-spacing-x);
  background-color: transparent;
  color: var(--bsi-form-control-text-color);
  font-size: var(--bsi-label-font-size-s);
  font-weight: var(--bsi-font-weight-solid);
}

::placeholder {
  color: var(--bsi-form-control-placeholder-color);
}

input::-webkit-datetime-edit {
  background-color: var(--bsi-color-background-primary-lighter);
  color: var(--bsi-form-contro-text-color);
}

.form-group {
  position: relative;
  margin-bottom: var(--bsi-form-group-spacing-y);
}
.form-group label.input-symbol-label:not(.active) {
  left: 2.25rem;
}
.form-group small.form-text {
  margin: 0;
  font-size: 0.875rem;
}
.form-group input[type=time] ~ label {
  font-size: 0.875rem;
}

.form-text {
  margin: var(--bsi-spacing-xxs) 0;
  color: var(--bsi-color-text-secondary);
}

.form-group.active .form-file-name {
  padding-bottom: 1.95rem;
}

.warning-feedback {
  display: none;
  width: 100%;
  margin-top: 0.25rem;
  color: var(--bsi-color-text-warning);
  font-size: 0.75rem;
}

.valid-feedback,
.invalid-feedback,
.warning-feedback {
  margin-left: 0.5rem;
}

.input-group .input-group-text .btn {
  border-radius: var(--bsi-form-control-border-radius) 0 0 var(--bsi-form-control-border-radius);
}
.input-group .input-group-append {
  margin-left: 0;
}
.input-group .input-group-append .btn {
  height: 100%;
  padding-top: 0;
  padding-bottom: 0;
  border-bottom: 1px solid hsl(210, 17%, 44%);
  border-radius: 0 var(--bsi-form-control-border-radius) var(--bsi-form-control-border-radius) 0;
}

.form-check {
  position: relative;
}
.form-check + .form-check {
  margin-top: var(--bsi-spacing-s);
}
.form-check [type=checkbox],
.form-check [type=radio] {
  position: absolute;
  top: 0;
  left: 0;
  width: auto;
  margin-top: 0;
  margin-left: 0;
  opacity: 0;
}
.form-check [type=checkbox] + label,
.form-check [type=radio] + label {
  position: relative;
  margin-bottom: 0;
  padding-left: 28px;
  font-size: var(--bsi-label-font-size);
  font-weight: var(--bsi-font-weight-solid);
  cursor: pointer;
  user-select: none;
}
@media (min-width: 576px) {
  .form-check [type=checkbox] + label,
  .form-check [type=radio] + label {
    font-size: var(--bsi-label-font-size-l);
  }
}
.form-check [type=checkbox]:focus + label,
.form-check [type=radio]:focus + label {
  border-color: hsl(0, 0%, 0%) !important;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
  outline: 3px solid transparent !important;
  outline-offset: 3px !important;
}
.form-check [type=checkbox]:focus[data-focus-mouse=true] + label,
.form-check [type=radio]:focus[data-focus-mouse=true] + label {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}
.form-check input[type=checkbox] + label::after,
.form-check input[type=checkbox] + label::before {
  position: absolute;
  left: 0;
  z-index: 1;
  content: "";
  border-width: 2px;
  border-style: solid;
  transition: all var(--bsi-transition-instant) ease-out;
}
.form-check input[type=checkbox] + label::after {
  top: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--bsi-form-control-border-radius);
}
.form-check input[type=checkbox]:checked + label::before {
  top: 3px;
  left: 3px;
  width: 6px;
  height: 12px;
  border-width: 2px;
  border-style: solid;
  border-color: transparent var(--bsi-color-border-inverse) var(--bsi-color-border-inverse) transparent;
  opacity: 1;
  transform: rotate(40deg);
  transform-origin: 100% 100%;
  backface-visibility: hidden;
}
.form-check input[type=checkbox]:checked + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
}
.form-check input[type=checkbox]:not(:checked) + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checkbox-border-color);
  background-color: transparent;
}
.form-check input[type=checkbox]:not(:checked) + label::before {
  top: 10px;
  left: 6px;
  width: 0;
  height: 0;
  border-color: transparent;
}
.form-check input[type=checkbox] + label:hover::after {
  border-color: var(--bsi-color-border-secondary-hover);
}
.form-check input[type=checkbox]:disabled + label {
  opacity: 1;
  cursor: not-allowed;
}
.form-check input[type=checkbox]:disabled:not(:checked) + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: transparent;
}
.form-check input[type=checkbox]:disabled:checked + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio] + label::after, .form-check input[type=radio] + label::before {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  content: "";
  width: 20px;
  height: 20px;
  border-width: 2px;
  border-style: solid;
  border-radius: var(--bsi-radius-rounded);
  transition: all var(--bsi-transition-instant) ease-out;
}
.form-check input[type=radio]:not(:checked) + label::after, .form-check input[type=radio]:not(:checked) + label::before {
  border-color: var(--bsi-form-checkbox-border-color);
}
.form-check input[type=radio]:not(:checked) + label:after {
  z-index: -1;
  transform: scale(0);
}
.form-check input[type=radio]:checked + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
  transform: scale(0.64);
}
.form-check input[type=radio]:checked + label::before {
  border-color: var(--bsi-form-checked-color);
}
.form-check input[type=radio] + label:hover::before {
  border-color: var(--bsi-color-border-secondary-hover);
}
.form-check input[type=radio]:disabled + label {
  cursor: not-allowed;
}
.form-check input[type=radio]:disabled:not(:checked) + label::after, .form-check input[type=radio]:disabled:not(:checked) + label::before {
  border-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio]:disabled:checked + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio]:disabled:checked + label::before {
  border-color: var(--bsi-color-border-disabled);
}
.form-check .form-text {
  display: block;
  margin-bottom: 0.5rem;
  padding-right: 3.25rem;
}
.form-check.form-check-group {
  margin-bottom: 1rem;
  padding: 0 0 1rem 0;
  box-shadow: inset 0 -1px 0 0 rgba(1, 1, 1, 0.1);
}
.form-check.form-check-group input[type=checkbox] + label,
.form-check.form-check-group input[type=radio] + label {
  position: static;
  padding-right: 3.25rem;
  padding-left: 0;
}
.form-check.form-check-group input[type=checkbox] + label::after, .form-check.form-check-group input[type=checkbox] + label::before,
.form-check.form-check-group input[type=radio] + label::after,
.form-check.form-check-group input[type=radio] + label::before {
  right: 0;
  left: auto;
}
.form-check.form-check-group input[type=checkbox]:checked + label::before {
  right: 11px;
}
.form-check.form-check-group input[type=radio]:checked + label::before {
  right: 0;
}
.form-check.form-check-group input.semi-checked:not(:checked) + label::before {
  right: 4px;
  left: auto;
}
.form-check input.semi-checked:not(:checked) + label::before {
  top: 12px;
  left: 4px;
  width: 12px;
  height: 2px;
  border-width: 0;
  border-style: none;
  border-color: transparent;
  opacity: 1;
  background: var(--bsi-color-background-inverse);
  transform: none;
  backface-visibility: hidden;
}
.form-check input.semi-checked:not(:checked) + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
}

.form-check-inline {
  display: inline-block;
}
.form-check-inline:not(:last-child) {
  margin-right: var(--bsi-spacing-m);
}

@media (prefers-reduced-motion: reduce) {
  fieldset legend,
  .form-group label,
  textarea,
  .form-check [type=checkbox],
  .form-check [type=radio],
  .form-check [type=checkbox] + label::after,
  .form-check [type=checkbox] + label::before,
  .form-check [type=radio] + label::after,
  .form-check [type=radio] + label::before,
  .toggles label input[type=checkbox] + .lever::before,
  .toggles label input[type=checkbox] + .lever::after {
    transition: none !important;
  }
}
.form-control-plaintext {
  border: 0;
  --bsi-form-control-border-color: transparent;
  --bsi-form-control-border-radius: 0;
  --bsi-form-control-background-color: transparent;
  --bsi-form-control-spacing: 0;
}
.form-control-plaintext:focus {
  outline: 0;
  box-shadow: none !important;
}
.form-control-plaintext + label {
  cursor: text;
}

.form-control {
  background-repeat: no-repeat;
  background-position: center right;
  background-size: 45px 20px;
}
.form-control:disabled {
  cursor: not-allowed;
  background: var(--bsi-color-background-disabled);
  border: 0;
  color: var(--bsi-color-text-disabled);
}

.form-control-sm {
  --bsi-form-control-spacing: var(--bsi-spacing-xxs) var(--bsi-spacing-3xs);
  --bsi-form-control-font-size: var(--bsi-label-font-size);
}
.form-control-sm::file-selector-button {
  padding: 0.25rem 0.5rem;
  margin: -0.25rem -0.5rem;
  margin-inline-end: 0.5rem;
}

.form-control-lg {
  --bsi-form-control-font-size: var(--bsi-lead-font-size);
}
.form-control-lg::file-selector-button {
  padding: 0.5rem 1rem;
  margin: -0.5rem -1rem;
  margin-inline-end: 1rem;
}

textarea.form-control {
  min-height: 2.5rem;
  border: 1px solid hsl(210, 17%, 44%);
}
textarea.form-control-sm {
  min-height: calc(1.5em + 0.5rem);
}
textarea.form-control-lg {
  min-height: calc(1.5em + 1rem);
}

.form-control-color {
  width: 3rem;
  height: 2.5rem;
  padding: 0.375rem;
}
.form-control-color:not(:disabled):not([readonly]) {
  cursor: pointer;
}
.form-control-color::-moz-color-swatch {
  border: 0 !important;
  border-radius: 0;
}
.form-control-color::-webkit-color-swatch {
  border-radius: 0;
}
.form-control-color.form-control-sm {
  height: calc(1.5em + 0.5rem);
}
.form-control-color.form-control-lg {
  height: calc(1.5em + 1rem);
}

.form-feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.75rem;
}

.input-group-text:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) ~ .input-group-text,
button:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) + button {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}

.input-group-text:has(~ .is-invalid),
.is-invalid ~ .input-group-text,
button:has(~ .is-invalid),
.is-invalid + button {
  border-color: var(--bsi-color-border-danger) !important;
}

.sr-only-justvalidate-bi {
  display: none;
}

.is-valid,
.is-invalid {
  padding-right: calc(1.5em + 0.75rem) !important;
}
.is-valid[type=number],
.is-invalid[type=number] {
  padding-right: calc(1.5em + 0.75rem + var(--bsi-form-control-spacing) + 1rem) !important;
}

.was-validated &:valid,
.is-valid {
  border-color: var(--bsi-color-border-success) !important;
  background-image: url('data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="%23008055" xmlns="http://www.w3.org/2000/svg"><path d="M9.6,16.9L4,11.4l0.8-0.7l4.8,4.8l8.5-8.4l0.7,0.7L9.6,16.9z" /></svg>');
}
.was-validated &:valid ~ .warning-feedback,
.is-valid ~ .warning-feedback {
  display: block;
}

.was-validated &:invalid,
.is-invalid {
  border-color: var(--bsi-color-border-danger) !important;
  background-image: url('data:image/svg+xml,<svg width="24" fill="%23cc334d" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12.7,12l6.7,6.6l-0.8,0.8L12,12.7l-6.6,6.7l-0.8-0.8l6.7-6.6L4.6,5.4l0.8-0.8l6.6,6.7l6.6-6.7l0.8,0.8L12.7,12z" /></svg>');
}

.form-control.warning {
  border-color: var(--bsi-color-border-warning) !important;
  background-image: url('data:image/svg+xml,<svg width="24" height="24" fill="%23995c00" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12.5,17h-1V2h1V17z M12.5,20h-1v2h1V20z" /></svg>');
}

.input-group-text:has(~ .is-valid),
.is-valid ~ .input-group-text,
button:has(~ .is-valid),
.is-valid + button {
  border-color: var(--bsi-color-border-success);
}

.is-valid + .input-group-text.align-buttons,
.is-invalid + .input-group-text.align-buttons {
  right: 30px;
}

.is-invalid + .input-group-text.align-buttons {
  bottom: 22px;
}

.autocomplete__wrapper .form-feedback {
  position: absolute;
}

textarea.form-control {
  background-position: top 0.5em right !important;
}

.text-primary {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-primary-rgb), var(--bsi-text-opacity)) !important;
}

.text-secondary {
  --bsi-text-opacity: 1;
  color: hsl(210, 33%, 28%) !important;
}

.text-success {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-success-rgb), var(--bsi-text-opacity)) !important;
}

.text-info {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-info-rgb), var(--bsi-text-opacity)) !important;
}

.text-warning {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-warning-rgb), var(--bsi-text-opacity)) !important;
}

.text-danger {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-danger-rgb), var(--bsi-text-opacity)) !important;
}

.text-light {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-light-rgb), var(--bsi-text-opacity)) !important;
}

.text-dark {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-dark-rgb), var(--bsi-text-opacity)) !important;
}

.text-black {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-black-rgb), var(--bsi-text-opacity)) !important;
}

.text-white {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-white-rgb), var(--bsi-text-opacity)) !important;
}

.text-100 {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-100-rgb), var(--bsi-text-opacity)) !important;
}

.text-200 {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-200-rgb), var(--bsi-text-opacity)) !important;
}

.text-300 {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-300-rgb), var(--bsi-text-opacity)) !important;
}

.text-400 {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-400-rgb), var(--bsi-text-opacity)) !important;
}

.text-500 {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-500-rgb), var(--bsi-text-opacity)) !important;
}

.text-600 {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-600-rgb), var(--bsi-text-opacity)) !important;
}

.text-700 {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-700-rgb), var(--bsi-text-opacity)) !important;
}

.text-800 {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-800-rgb), var(--bsi-text-opacity)) !important;
}

.text-900 {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-900-rgb), var(--bsi-text-opacity)) !important;
}

.text-body {
  --bsi-text-opacity: 1;
  color: rgba(var(--bsi-body-color-rgb), var(--bsi-text-opacity)) !important;
}

.text-muted {
  --bsi-text-opacity: 1;
  color: hsl(210, 17%, 44%) !important;
}

.text-black-50 {
  --bsi-text-opacity: 1;
  color: rgba(0, 0, 0, 0.5) !important;
}

.text-white-50 {
  --bsi-text-opacity: 1;
  color: rgba(255, 255, 255, 0.5) !important;
}

.text-reset {
  --bsi-text-opacity: 1;
  color: inherit !important;
}

:host {
  display: block;
}`;

class ItCheckboxGroupBase extends FormControl {
    constructor() {
        super(...arguments);
        this.required = false;
        /** Draws checkboxes inline, side by side. */
        this.inline = false;
        /** Draws checkboxes in groups. */
        this.group = false;
        this.name = ''; // Il name del gruppo
        // Override del controller per usare le funzioni di validazione di gruppo
        this.formControlController = new FormControlController(this, {
            // Il valore è un array dei valori delle checkbox selezionate
            value: (control) => control.checkboxes.filter((cb) => cb.checked).map((cb) => cb.value || 'true'),
            // Il name è quello del gruppo
            name: (control) => control.name,
            // La validità è gestita da noi con checkValidity di gruppo
            checkValidity: (control) => control.checkValidity(),
            reportValidity: (control) => control.reportValidity(),
            // Non implementiamo setValue qui, gestito dalle singole checkbox
            setValue: () => { },
            // Ascoltiamo l'input su tutti i cambi di stato delle checkbox
            assumeInteractionOn: ['it-change'],
        });
        // Questo state servirà per forzare il re-render del messaggio di errore
        this._groupValid = true;
    }
    get selectedValues() {
        if (!this.value)
            return [];
        try {
            return JSON.parse(this.value);
        }
        catch {
            return [];
        }
    }
    set selectedValues(vals) {
        this.value = JSON.stringify(vals);
    }
    has(v) {
        return this.selectedValues.indexOf(v) >= 0;
    }
    connectedCallback() {
        super.connectedCallback?.();
        this.addEventListener('it-change', this._handleGroupChange);
    }
    disconnectedCallback() {
        super.disconnectedCallback?.();
        this.removeEventListener('it-change', this._handleGroupChange);
    }
    /**
     * Override: Custom validity check for radio group
     */
    get validity() {
        // Fallback: create a custom ValidityState-like object
        const valid = !this.required || !!this.value;
        return {
            valid,
            valueMissing: this.required && !this.value,
            typeMismatch: false,
            patternMismatch: false,
            tooLong: false,
            tooShort: false,
            rangeUnderflow: false,
            rangeOverflow: false,
            stepMismatch: false,
            badInput: false,
            customError: this.customValidation && this.validationText?.length > 0,
        };
    }
    // Ascolta gli 'it-change' bubblati dalle singole checkbox
    _handleGroupChange() {
        // Aggiorna this.value con l'array corrente delle checkbox selezionate
        const selected = this.checkboxes.filter((cb) => cb.checked).map((cb) => cb.value || 'true');
        this.value = JSON.stringify(selected);
        // Quando qualcosa cambia, ricalcola la validità
        this.handleValidationMessages();
        this.requestUpdate(); // Aggiorna la vista
    }
    /**
     * Override di checkValidity per la logica di gruppo.
     */
    checkValidity() {
        if (!this.required) {
            return true; // Non è obbligatorio
        }
        // Almeno una checkbox deve essere selezionata
        const atLeastOneChecked = this.checkboxes.some((cb) => cb.checked);
        // Aggiorna lo stato di validità del gruppo
        this._groupValid = atLeastOneChecked;
        return atLeastOneChecked;
    }
    /**
     * Override di reportValidity per mostrare l'errore a livello di gruppo.
     */
    reportValidity() {
        // Chiama la logica di validazione personalizzata
        const isValid = this.checkValidity();
        // Aggiorna il messaggio di validazione se invalido
        if (!isValid) {
            this.validationMessage = this.$t('validityGroupRequired'); // Usa la tua chiave di traduzione per "Campo obbligatorio"
        }
        else {
            this.validationMessage = '';
        }
        // Forzare l'aggiornamento di FormControlController che gestirà data-invalid/data-valid
        this.formControlController.updateValidity();
        this.requestUpdate();
        return isValid;
    }
    /**
     * Il tuo FormControl ha già un handleValidationMessages che
     * setta this.validationMessage. Aggiorniamolo per la logica di gruppo.
     */
    handleValidationMessages() {
        if (!this.customValidation && this.formControlController.submittedOnce) {
            this.validationMessage = this.checkValidity() ? '' : this.$t('validityGroupRequired');
        }
        if (this.customValidation) {
            this.validationMessage = this.inputElement.validationMessage;
        }
    }
    /**
     * Sync group state (name, grouped, inline, required, disabled, checked) to child checkboxes
     * This replaces the need for requestUpdate() calls
     */
    _syncGroupStateToChildren() {
        if (!this.checkboxes || this.checkboxes.length === 0) {
            return;
        }
        this.checkboxes.forEach((cb) => {
            // eslint-disable-next-line no-param-reassign
            cb.name = this.name;
            // eslint-disable-next-line no-param-reassign
            cb.group = this.group || !this.inline;
            // eslint-disable-next-line no-param-reassign
            cb.inline = this.inline;
            // eslint-disable-next-line no-param-reassign
            cb.required = this.required;
            if (this.disabled) {
                // eslint-disable-next-line no-param-reassign
                cb.disabled = this.disabled;
            }
            if (!cb.hasAttribute('checked')) {
                // eslint-disable-next-line no-param-reassign
                cb.checked = this.has(cb.value);
            }
        });
    }
    updated(changed) {
        super.updated(changed);
        // Update radios when value or name changes
        if (changed.has('value') || (changed.has('validationText') && this.customValidation)) {
            // Re-validate after value change (for native validation) only if validation was already triggered
            this.handleValidationMessages();
        }
        // If relevant group properties changed, sync to child checkboxes
        const relevant = ['group', 'inline', 'name', 'required', 'disabled', 'value'];
        const hasChanged = Array.from(changed.keys()).some((k) => relevant.includes(String(k)));
        if (hasChanged && this.checkboxes?.length) {
            this._syncGroupStateToChildren();
        }
    }
    // Render per mostrare l'errore (usa la tua logica di rendering dell'errore)
    // All'interno di ItCheckboxGroup (metodo render)
    render() {
        const showValidation = this.formControlController.submittedOnce || this.customValidation; // true; // this._touched || this.customValidation;
        const validityMessage = (showValidation ? this.validationMessage : '') ?? '';
        // Determina lo stato di invalidità in base alla validazione di gruppo
        const invalid = validityMessage?.length > 0 || !this._groupValid;
        const errorId = `invalid-feedback-${this._id}`;
        // Messaggio di validazione per il gruppo
        const validityMessageRender = html `<div
      role="alert"
      id="${errorId}"
      class="form-feedback text-danger"
      ?hidden=${!(validityMessage?.length > 0)}
    >
      <span class="visually-hidden">${this._label?.[0]?.textContent || ''}: </span>
      ${validityMessage}
    </div>`;
        // il pattern per errori nel form è stato preso da qui https://design-system.w3.org/styles/form-errors.html
        return html `
      <fieldset
        class="it-checkbox-group it-form__control"
        id="${this._id}"
        aria-describedby=${ifDefined(invalid ? errorId : undefined)}
        aria-required=${ifDefined(this.required ? 'true' : undefined)}
        part="fieldset"
      >
        <legend part="legend">
          <slot name="legend"></slot>
        </legend>

        <slot @slotchange=${this._handleSlotChange}></slot>
        ${when(invalid, () => validityMessageRender)}
      </fieldset>
    `;
    }
    setCustomValidity(message) {
        if (!this.customValidation) {
            this.inputElement.setCustomValidity(message);
            this.validationMessage = this.inputElement.validationMessage;
        }
        else {
            this.validationMessage = message;
        }
        this.formControlController.updateValidity();
    }
    // Metodo per gestire l'assegnazione iniziale e re-assegnazione delle checkbox
    _handleSlotChange() {
        // Dopo l'assegnazione, forziamo un controllo di validità per inizializzare _groupValid
        this.handleValidationMessages();
        this.requestUpdate();
    }
}
ItCheckboxGroupBase.styles = styles;
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItCheckboxGroupBase.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItCheckboxGroupBase.prototype, "inline", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItCheckboxGroupBase.prototype, "group", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItCheckboxGroupBase.prototype, "name", void 0);
__decorate([
    queryAssignedElements({ selector: 'it-checkbox' }),
    __metadata("design:type", Array)
], ItCheckboxGroupBase.prototype, "checkboxes", void 0);
__decorate([
    queryAssignedElements({ slot: 'legend' }),
    __metadata("design:type", Array)
], ItCheckboxGroupBase.prototype, "_label", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItCheckboxGroupBase.prototype, "_groupValid", void 0);

export { ItCheckboxGroupBase };
//# sourceMappingURL=it-checkbox-group-base.js.map
